/**
 * @file loginLocation.ts
 * @brief Presentation helpers for login-location display (flag emoji + label).
 *
 * @details
 * Shared by the super-admin Users feature and the company-admin Employees
 * feature (both surface the same login_events data). Pure functions — no React.
 *
 * @author DES
 * @date 08-07-2026
 */

/**
 * ISO 3166-1 alpha-2 country code → flag emoji (regional indicators).
 * Returns "" for missing/invalid codes.
 */
export function countryFlag(code?: string | null): string {
  const cc = (code || "").trim().toUpperCase();
  if (cc.length !== 2 || !/^[A-Z]{2}$/.test(cc)) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + (cc.charCodeAt(0) - 65),
    A + (cc.charCodeAt(1) - 65),
  );
}

/**
 * Human-readable location from a login event / user row.
 * Prefers "City, Region, Country"; falls back through what's present.
 * Returns "" when nothing is known (e.g. private/loopback IP).
 */
export function formatLoginLocation(parts: {
  country?: string | null;
  region?: string | null;
  city?: string | null;
}): string {
  const bits = [parts.city, parts.region, parts.country].filter(
    (b): b is string => Boolean(b && b.trim()),
  );
  return bits.length ? bits.join(", ") : "";
}

/**
 * Structural shape of one login_events row as the UI consumes it. Both the
 * Users (super-admin) and Employees (company-admin) schemas satisfy this.
 */
export interface LoginEventRow {
  id?: string;
  ip_address?: string | null;
  country?: string | null;
  region?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  timezone?: string | null;
  user_agent?: string | null;
  created_at?: string | null;
}

// ----------------------------------------------------------------------
// Which app a login came from (03-10-2026, DES: "login history mein web,
// desktop aur mobile alag dikhao"). From the stored user agent, whose shapes
// in login_events are:
//   phone   "VerblizrRN/38 CFNetwork/3896.100.1.2.1 Darwin/27.0.0"
//   desktop "Mozilla/5.0 (Macintosh; …) verblizr-desktop/0.1.0 Chrome/… Electron"
//   web     any browser ("Mozilla/5.0 (Macintosh; …) Chrome/150… Safari/537.36")
// Twin of FRONTEND/src/shared/utils/loginLocation.ts `loginClient`.
// ----------------------------------------------------------------------
export type LoginClientKind = "mobile" | "desktop" | "web" | "unknown";

export interface LoginClient {
  kind: LoginClientKind;
  /** "Mobile app" | "Desktop app" | "Web" | "Unknown device" */
  label: string;
  /** e.g. "iOS · build 38", "macOS · v0.1.0", "Chrome · macOS"; null if none. */
  detail: string | null;
}

function uaOs(ua: string): string | null {
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh|Mac OS X/.test(ua)) return "macOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return null;
}

function uaBrowser(ua: string): string | null {
  if (/Edg\//.test(ua)) return "Edge";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Version\/[\d.]+.*Safari\//.test(ua)) return "Safari";
  return null;
}

export function loginClient(userAgent?: string | null): LoginClient {
  const ua = (userAgent ?? "").trim();
  if (!ua) return { kind: "unknown", label: "Unknown device", detail: null };

  const rn = ua.match(/^VerblizrRN\/(\d+)/);
  if (rn || /^okhttp\//i.test(ua)) {
    const os = /Darwin|CFNetwork/.test(ua) ? "iOS" : /okhttp|Android/i.test(ua) ? "Android" : null;
    const parts = [os, rn ? `build ${rn[1]}` : null].filter(Boolean);
    return { kind: "mobile", label: "Mobile app", detail: parts.length ? parts.join(" · ") : null };
  }

  const desk = ua.match(/verblizr-desktop\/([\d.]+)/i);
  if (desk || /Electron/.test(ua)) {
    const parts = [uaOs(ua), desk ? `v${desk[1]}` : null].filter(Boolean);
    return { kind: "desktop", label: "Desktop app", detail: parts.length ? parts.join(" · ") : null };
  }

  if (/^Mozilla\//.test(ua)) {
    const parts = [uaBrowser(ua), uaOs(ua)].filter(Boolean);
    return { kind: "web", label: "Web", detail: parts.length ? parts.join(" · ") : null };
  }

  return { kind: "unknown", label: "Unknown device", detail: null };
}
